import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["panel", "toggle", "mobileToggle", "backdrop", "content"]
  static values = { collapseLabel: String, expandLabel: String, menuLabel: String, closeMenuLabel: String }

  connect() {
    this.element.dataset.sidebarConnected = "true"
    this.media = window.matchMedia("(max-width: 767px)")
    this.onResize = () => { this.close(); this.sync() }
    this.onCache = () => this.close()
    this.onKey = (event) => this.trapFocus(event)
    this.onOutside = (event) => {
      this.panelTarget.querySelectorAll("details[open]").forEach((details) => {
        if (!details.contains(event.target)) details.open = false
      })
    }
    this.media.addEventListener("change", this.onResize)
    document.addEventListener("turbo:before-cache", this.onCache)
    this.panelTarget.addEventListener("keydown", this.onKey)
    document.addEventListener("click", this.onOutside)
    document.documentElement.classList.remove("sidebar-open")
    this.sync()
  }

  disconnect() {
    this.close()
    this.media?.removeEventListener("change", this.onResize)
    document.removeEventListener("turbo:before-cache", this.onCache)
    this.panelTarget.removeEventListener("keydown", this.onKey)
    document.removeEventListener("click", this.onOutside)
  }

  toggle() {
    if (this.media.matches) {
      const open = document.documentElement.classList.toggle("sidebar-open")
      this.sync()
      if (open) this.focusable()[0]?.focus()
      else this.mobileToggleTargets[0]?.focus()
      return
    }
    this.close()
    const collapsed = document.documentElement.classList.toggle("sidebar-collapsed")
    try { localStorage.setItem("sidebar-collapsed", collapsed ? "1" : "0") } catch (_) {}
    this.sync()
  }

  close(event) {
    const wasOpen = document.documentElement.classList.contains("sidebar-open")
    const disclosure = document.activeElement?.closest("details[open]")
    if (event?.key === "Escape" && disclosure && this.panelTarget.contains(disclosure)) {
      disclosure.open = false
      disclosure.querySelector("summary")?.focus()
      return
    }
    document.documentElement.classList.remove("sidebar-open")
    if (this.hasPanelTarget) this.panelTarget.querySelectorAll("details[open]").forEach((details) => { details.open = false })
    this.sync()
    if (wasOpen && this.media?.matches) this.mobileToggleTargets[0]?.focus()
  }

  sync() {
    const mobile = this.media?.matches || false
    const open = mobile && document.documentElement.classList.contains("sidebar-open")
    const expanded = !document.documentElement.classList.contains("sidebar-collapsed")
    this.toggleTargets.forEach((button) => {
      button.setAttribute("aria-expanded", String(expanded))
      button.setAttribute("aria-label", expanded ? this.collapseLabelValue : this.expandLabelValue)
    })
    this.mobileToggleTargets.forEach((button) => {
      button.setAttribute("aria-expanded", String(open))
      if (this.hasMenuLabelValue && this.hasCloseMenuLabelValue) {
        button.setAttribute("aria-label", open ? this.closeMenuLabelValue : this.menuLabelValue)
      }
    })
    if (this.hasBackdropTarget) this.backdropTarget.hidden = !open
    if (this.hasPanelTarget) this.panelTarget.inert = mobile && !open
    if (this.hasContentTarget) this.contentTarget.inert = open
    const footer = document.querySelector(".app-footer")
    if (footer) footer.inert = open
  }

  focusable() {
    return [...this.panelTarget.querySelectorAll("a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), summary")]
      .filter((element) => element.getClientRects().length && !element.closest("[hidden]"))
  }

  trapFocus(event) {
    if (event.key !== "Tab" || !this.media.matches || !document.documentElement.classList.contains("sidebar-open")) return
    const elements = this.focusable(), first = elements[0], last = elements[elements.length - 1]
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
  }
}
